import { useMemo, useState, useEffect } from 'react';
import { range } from 'lodash';
import moment from 'moment';

import { ThemeColors } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { CleaningScheduleTypeEnum, CleaningTaskStatusEnum, StringTranslation } from '@pimm/services/lib/sms-workforce';
import { useSiteTime } from '@app/features/store-core';
import { useKitchenPositionGuideLive } from '../context';
import { TaskItem, TileBuckets } from '../types';

type CategorizeTask = {
  cleaningTaskId?: number;
  cleaningTaskAssignmentId?: number;
  cleaningTaskStatus?: string;
  dueDate?: string;
  dayOfWeek?: string;
  scheduleType?: string;
  scheduleStartTime?: string;
  scheduleEndTime?: string;
  title?: string;
  titleTranslations?: StringTranslation[];
};

/**
 * Splits a flat task list into 4 buckets for the tile display.
 *
 * Display list  = todayOverdue + todayPending  (today only)
 * Overdue badge = todayOverdue + pastOverdue   (count)
 * Done badge    = todayCompleted               (count)
 * pastOverdue contributes to badge count only — NOT to the display list.
 *
 * Daypart-start gate applies to today only (past-day tasks bypass it).
 */
export const categorizeTasksIntoBuckets = (
  tasks: CategorizeTask[],
  now: moment.Moment,
  siteNow: Date,
  weekDays: Record<string, Date>,
  translate: (value?: string, translations?: StringTranslation[]) => string,
  startTimeMap?: Map<number, string>,
): TileBuckets => {
  const todayPending: TaskItem[] = [];
  const todayOverdue: TaskItem[] = [];
  const todayCompleted: TaskItem[] = [];
  const pastOverdue: TaskItem[] = [];

  for (const task of tasks) {
    if (task.cleaningTaskStatus === CleaningTaskStatusEnum.NotApplicable) continue;
    if (task.cleaningTaskId == null) continue;

    const isComplete = task.cleaningTaskStatus === CleaningTaskStatusEnum.Complete;
    const taskDayName = task.dayOfWeek;
    const taskMoment = taskDayName ? moment(weekDays[taskDayName]) : task.dueDate ? moment(task.dueDate.replace('Z', '')) : null;
    if (!taskMoment?.isValid()) continue;

    const isToday = taskMoment.isSame(now, 'day');
    const isPast = !isToday && taskMoment.isBefore(now, 'day');

    if (!isToday && !isPast) continue;

    const item: TaskItem = {
      cleaningTaskId: task.cleaningTaskId,
      title: translate(task.title, task.titleTranslations),
      scheduleType: task.scheduleType,
    };

    if (isComplete) {
      if (isToday) todayCompleted.push(item);
      continue;
    }

    if (isPast) {
      pastOverdue.push({ ...item, isOverdue: true });
      continue;
    }

    // scheduleStartTime gate — today only, Daily tasks only. "Daypart" is a daily concept;
    // Weekly/Monthly tasks are visible as soon as their due date arrives.
    // Null scheduleType defaults to Daily (matches shift-scope.tsx convention).
    // Employee tasks lack scheduleStartTime on their DTO — fall back to the position task lookup map.
    if ((task.scheduleType ?? CleaningScheduleTypeEnum.Daily) === CleaningScheduleTypeEnum.Daily) {
      const startTime = task.scheduleStartTime ?? startTimeMap?.get(task.cleaningTaskId!);
      if (startTime) {
        const [startHour, startMinute] = startTime.split(':').map(Number);
        if (siteNow.getHours() < startHour || (siteNow.getHours() === startHour && siteNow.getMinutes() < startMinute)) continue;
      }
    }

    let isOverdue: boolean;
    if (task.dueDate) {
      // Explicit dueDate takes priority — same logic as getEffectiveDueDate in shift-scope.
      isOverdue = moment(task.dueDate.replace('Z', '')).isBefore(now);
    } else if (taskDayName && task.scheduleEndTime) {
      // scheduleEndTime is store-local — compare hours directly, no datetime construction.
      const [endHour, endMinute] = task.scheduleEndTime.split(':').map(Number);
      isOverdue = now.hour() > endHour || (now.hour() === endHour && now.minute() >= endMinute);
    } else {
      isOverdue = taskMoment.isBefore(now);
    }

    const overdueItem = { ...item, isOverdue };
    if (isOverdue) todayOverdue.push(overdueItem);
    else todayPending.push(overdueItem);
  }

  return { todayPending, todayOverdue, todayCompleted, pastOverdue };
};

/**
 * 3-state cleaning icon color derived from union of position + emp buckets.
 * Priority: red (#F04438) > yellow (#FFB900) > gray (#D8D9DA)
 *
 *   red    — any overdue (todayOverdue + pastOverdue across both sections)
 *   yellow — active work pending today (todayPending across both sections)
 *   gray   — no active work (all done or nothing scheduled)
 */
export const deriveCleaningIconColor = (posBuckets: TileBuckets, empBuckets: TileBuckets): string => {
  const totalOverdue =
    posBuckets.todayOverdue.length + posBuckets.pastOverdue.length + empBuckets.todayOverdue.length + empBuckets.pastOverdue.length;
  const totalPending = posBuckets.todayPending.length + empBuckets.todayPending.length;
  if (totalOverdue > 0) return ThemeColors.error[500];
  if (totalPending > 0) return ThemeColors.primary[500];
  return ThemeColors.gray[300];
};

type UseCleaningTasksDataResult = {
  posBuckets: TileBuckets;
  empBuckets: TileBuckets;
  cleaningIconColor: string;
  positionTitle: string | undefined;
};

/**
 * Shared hook — computes cleaning task buckets, dedup, and icon state
 * for both the position tile and the shift-scope modal.
 *
 * Reads from KitchenPositionGuideLiveContext. Position tasks take priority
 * during deduplication (emp tasks filtered against this slot's position IDs).
 */
export const useCleaningTasksData = (slotNumber: number, employeeId: string | undefined): UseCleaningTasksDataResult => {
  const siteTime = useSiteTime();
  const { translate, locale } = useAppLocale();
  const livePositionGuide = useKitchenPositionGuideLive();

  // Recompute every minute so overdue status updates when scheduleEndTime passes
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(interval);
  }, []);

  return useMemo(() => {
    const siteNow = siteTime.today();
    const now = moment(siteNow);
    const weekStart = siteTime.toStartEndOfWeek(siteNow)?.startDate;
    const weekDays: Record<string, Date> = weekStart
      ? Object.fromEntries(
          range(7).map(i => {
            const day = moment(weekStart).add(i, 'days');
            return [day.locale('en-US').format('dddd'), day.toDate()];
          }),
        )
      : {};

    const positionEntry = livePositionGuide.data?.positionCleaningTasks?.find(entry => entry.slotNumber === slotNumber);

    const rawPositionTasks: CategorizeTask[] = positionEntry?.tasks ?? [];

    const employeeEntry = employeeId ? livePositionGuide.data?.cleaningTasks?.find(entry => entry.employeeId === employeeId) : undefined;

    const allEmpTasks: CategorizeTask[] = (employeeEntry?.scheduleTypes ?? []).flatMap(schedule =>
      (schedule.assignedTasks ?? []).map(task => ({
        ...task,
        scheduleType: schedule.scheduleType,
      })),
    );

    // Dedup: position tasks take priority — emp excludes any task ID already in position list
    const posTaskIds = new Set(rawPositionTasks.map(t => t.cleaningTaskId));
    const rawEmpTasks = allEmpTasks.filter(t => !posTaskIds.has(t.cleaningTaskId));

    // Build a cross-slot lookup map of cleaningTaskId → scheduleStartTime from all position tasks.
    // Employee task DTOs lack scheduleStartTime, so we fall back to the matching position task's value.
    const startTimeMap = new Map<number, string>();
    for (const entry of livePositionGuide.data?.positionCleaningTasks ?? []) {
      for (const task of entry.tasks ?? []) {
        if (task.cleaningTaskId != null && task.scheduleStartTime) {
          startTimeMap.set(task.cleaningTaskId, task.scheduleStartTime);
        }
      }
    }

    const posBuckets = categorizeTasksIntoBuckets(rawPositionTasks, now, siteNow, weekDays, translate, startTimeMap);
    const empBuckets = categorizeTasksIntoBuckets(rawEmpTasks, now, siteNow, weekDays, translate, startTimeMap);
    const cleaningIconColor = deriveCleaningIconColor(posBuckets, empBuckets);

    return {
      posBuckets,
      empBuckets,
      cleaningIconColor,
      positionTitle: translate(positionEntry?.positionTitle, positionEntry?.positionTitleTranslations) ?? undefined,
    };
  }, [slotNumber, employeeId, livePositionGuide.data, locale, tick]);
};
